import React, { useEffect, useMemo, useState } from 'react';
import apiConfig from '../config/api';
import AnalyticsService from '../services/AnalyticsService';

function decodePayload(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(raw)))));
  } catch {
    return null;
  }
}

const PermitDetailPage = () => {
  const [record, setRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);

  const pathId = useMemo(() => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    return parts[1] || '';
  }, []);

  const payload = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return decodePayload(params.get('data'));
  }, []);

  useEffect(() => {
    if (!pathId) return;
    AnalyticsService.trackPermitDetailOpen({
      requestNumber: pathId,
      permitNumber: payload?.permitNumber || payload?.permit_number,
      title: payload?.title,
      municipality: payload?.municipality || payload?.county,
      sourceSurface: 'permit_detail_page',
    });
  }, [pathId, payload]);

  useEffect(() => {
    const loadRecord = async () => {
      if (!pathId) return;
      setLoadingRecord(true);
      try {
        const res = await fetch(`${apiConfig.apiBaseUrl}/api/ta-records?kind=permit&id=${encodeURIComponent(pathId)}`);
        if (!res.ok) return;
        const data = await res.json();
        setRecord(data.record || null);
        if (Array.isArray(data.links) && data.links.length > 0) setLinks(data.links);
      } catch {
        setRecord(null);
      } finally {
        setLoadingRecord(false);
      }
    };
    loadRecord();
  }, [pathId]);

  useEffect(() => {
    const resolveLinks = async () => {
      const source = record || payload;
      if (!source || links.length > 0) return;
      setLoadingLinks(true);
      try {
        const params = new URLSearchParams({
          url: source.plan_url || source.planUrl || '',
          municipality: source.municipality || source.county || '',
          source: source.dataSource || source.source || 'tel_aviv_municipal',
          planNumber: source.request_number || source.requestNumber || source.planNumber || pathId || '',
          planTitle: source.title || '',
        });
        const res = await fetch(`${apiConfig.apiBaseUrl}/api/plan-links?${params.toString()}`);
        const data = await res.json();
        setLinks(data.links || []);
      } catch {
        setLinks([]);
      } finally {
        setLoadingLinks(false);
      }
    };
    resolveLinks();
  }, [payload, record, links.length, pathId]);

  const display = record || payload;
  const goBack = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  if (!display && !loadingRecord) {
    return (
      <div className="legal-page-container" style={{ direction: 'rtl' }}>
        <h1>פרטי היתר</h1>
        <p>לא נמצאו פרטי היתר לשיתוף הזה.</p>
        <button className="action-btn secondary" onClick={goBack}>חזרה</button>
      </div>
    );
  }

  const rows = [
    ['מספר בקשה', display?.request_number || display?.requestNumber || '-'],
    ['מספר היתר', display?.permit_number || display?.permitNumber || '-'],
    ['כותרת', display?.title || '-'],
    ['סטטוס', display?.status || '-'],
    ['סוג בקשה', display?.request_type || display?.requestType || '-'],
    ['מסלול רישוי', display?.licensing_track || display?.licensingTrack || '-'],
    ['תאריך פתיחה', display?.submission_date || display?.submissionDate || '-'],
    ['תאריך היתר', display?.approval_date || display?.approvalDate || '-'],
    ['תאריך תפוגה', display?.expiry_date || display?.expiryDate || '-'],
    ['כתובת', display?.address || '-'],
    ['מספר בניין', display?.building_number || display?.buildingNumber || '-'],
    ['רשות', display?.municipality || display?.county || 'תל אביב-יפו'],
  ];

  return (
    <div className="legal-page-container" style={{ direction: 'rtl' }}>
      <h1 style={{ marginBottom: 8 }}>היתר #{display?.request_number || display?.requestNumber || display?.planNumber || pathId}</h1>
      <p style={{ marginTop: 0, color: '#6b7280' }}>{display?.dataSource || display?.source || 'מאגר עירוני תל אביב'}</p>
      {loadingRecord ? <p>טוען נתוני היתר...</p> : null}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
        {rows.map(([label, value]) => (
          <React.Fragment key={label}>
            <strong style={{ color: '#334155' }}>{label}</strong>
            <span style={{ color: '#0f172a' }}>{value || '-'}</span>
          </React.Fragment>
        ))}
      </div>

      {display?.content ? (
        <div style={{ marginTop: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
          <h3 style={{ marginTop: 0 }}>תוכן בקשה</h3>
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{display.content}</div>
        </div>
      ) : null}

      <h3>קישורים זמינים</h3>
      {loadingLinks ? <p>בודק קישורים...</p> : null}
      {!loadingLinks && links.length === 0 ? <p>לא נמצאו קישורים זמינים כרגע.</p> : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {links.map((link) => (
          <button
            key={link.url}
            className="action-btn secondary"
            disabled={!link.available}
            onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
            title={link.available ? 'פתח קישור' : `לא זמין (${link.reason})`}
          >
            {link.label}{link.available ? '' : ' (לא זמין)'}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 20 }}>
        <button className="action-btn secondary" onClick={goBack}>חזרה לתוצאות</button>
      </div>
    </div>
  );
};

export default PermitDetailPage;
